import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProprieteAffichee } from 'src/app/bo/proprieteAffichee';
import { SiteApiKey } from 'src/app/bo/siteApiKey';
import { CreatedSiteApiKey } from 'src/app/bo/createdSiteApiKey';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { HttpService } from 'src/app/services/http.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { MineTrackerModalComponent } from '../mine-tracker-modal/mine-tracker-modal.component';
import { ApiKeyDisplayModalComponent } from '../api-key-display-modal/api-key-display-modal.component';

@Component({
  selector: 'app-mine-tracker',
  templateUrl: './mine-tracker.component.html',
  styleUrls: ['./mine-tracker.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule],
})
export class MineTrackerComponent {

  private readonly http = inject(HttpService);
  protected readonly utilService = inject(UtilisateurService);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly modalService = inject(BsModalService);

  protected readonly tabApiKeys = signal<SiteApiKey[]>([]);
  protected readonly activeSort = signal<number | undefined>(undefined);
  protected readonly arrowSort = signal<boolean[]>([]);
  protected readonly proprietesAffichees: ProprieteAffichee[];

  private mineTrackerModal: BsModalRef;
  private apiKeyDisplayModal: BsModalRef;

  constructor() {
    this.proprietesAffichees = new SiteApiKey().getProprietesAffichees();
    this.arrowSort.set(this.proprietesAffichees.map(() => false));

    // Re-fetch whenever the active site changes
    effect(() => {
      const site = this.utilService.currentSite();
      if (site?.siteId) {
        this.getApiKeysBySiteId();
      } else {
        this.tabApiKeys.set([]);
      }
    });

    this.modalService.onHide.pipe(takeUntilDestroyed()).subscribe((reason: string) => {
      if (reason === 'saved') {
        this.getApiKeysBySiteId();
      }
    });
  }

  protected selectApiKey(event: Event, apiKey: SiteApiKey): void {
    event.stopPropagation();

    this.mineTrackerModal = this.modalService.show(MineTrackerModalComponent, {
      class: 'modal-lg',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      initialState: {
        siteId: this.utilService.currentSite()?.siteId,
        selectedApiKey: Object.assign(new SiteApiKey(), apiKey),
        isModif: true,
      },
    });
  }

  protected ouvrirModaleAdd(): void {
    this.mineTrackerModal = this.modalService.show(MineTrackerModalComponent, {
      class: 'modal-lg',
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      initialState: {
        siteId: this.utilService.currentSite()?.siteId,
        selectedApiKey: new SiteApiKey(),
        isModif: false,
        onApiKeyCreated: (createdApiKey: CreatedSiteApiKey) => {
          this.showApiKeyDisplayModal(createdApiKey);
        },
      },
    });
  }

  private showApiKeyDisplayModal(createdApiKey: CreatedSiteApiKey): void {
    this.apiKeyDisplayModal = this.modalService.show(ApiKeyDisplayModalComponent, {
      class: 'modal-lg',
      backdrop: true,
      ignoreBackdropClick: false,
      keyboard: true,
      initialState: { createdApiKey },
    });

    this.apiKeyDisplayModal.onHide.subscribe(() => {
      this.getApiKeysBySiteId();
    });
  }

  private getApiKeysBySiteId(): void {
    this.fonctionsService.loading_screen(true);
    this.tabApiKeys.set([]);

    const url = 'SiteApiKeys/' + this.utilService.currentSite()?.siteId;

    this.http.get<SiteApiKey[]>(url).subscribe({
      next: result => {
        this.tabApiKeys.set(result);
        this.fonctionsService.loading_screen(false);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 401 || error.status === 403) {
          console.log(error.error);
        }
        this.fonctionsService.launch_toast(false);
        this.fonctionsService.loading_screen(false);
      },
    });
  }

  protected sortBy(propriete: ProprieteAffichee, i: number): void {
    const updated = this.arrowSort().map((v, idx) => idx === i ? !v : v);
    this.arrowSort.set(updated);
    this.activeSort.set(i);

    const sorted = [...this.tabApiKeys()].sort((a, b) => {
      const aVal = a[propriete.nom];
      const bVal = b[propriete.nom];
      return updated[i]
        ? (aVal < bVal ? -1 : aVal > bVal ? 1 : 0)
        : (bVal < aVal ? -1 : bVal > aVal ? 1 : 0);
    });
    this.tabApiKeys.set(sorted);
  }
}
